import React, { Fragment, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Package,
  Warehouse,
  CreditCard,
  ShoppingBag,
  Truck,
  Heart,
  Star,
  TicketPercent,
  LayoutDashboard,
  Bell,
  MessageCircle,
  Coins,
  Shirt,
  Smartphone,
  HeartPulse,
  ShoppingBasket,
  Sofa,
  Car,
  GraduationCap,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import "./e-commerce-development.css";

type CardItem = {
  title: string;
  text?: string;
  icon: string;
  iconUrl?: string;
};

type MetricItem = {
  value: string;
  label: string;
  icon: string;
};

type FeatureItem = {
  title: string;
  icon: LucideIcon;
};

type TechItem = {
  name: string;
  logo: string;
};

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  visible: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const stats: MetricItem[] = [
  { value: "500+", label: "Projects Delivered", icon: "PR" },
  { value: "100+", label: "Online Stores Built", icon: "OS" },
  { value: "95%", label: "Client Retention", icon: "CR" },
  { value: "10+", label: "Years Experience", icon: "YE" },
];

const whyChooseUs: CardItem[] = [
  {
    title: "Shopify Experts",
    text: "Certified Shopify experts building powerful and scalable stores.",
    icon: "Shopify",
    iconUrl: "https://cdn.simpleicons.org/shopify/7AB55C",
  },
  {
    title: "WooCommerce Specialists",
    text: "Custom WooCommerce solutions for all types of businesses.",
    icon: "WooCommerce",
    iconUrl: "https://cdn.simpleicons.org/woocommerce/96588A",
  },
  {
    title: "Custom Development",
    text: "Tailored solutions built to match your unique business model.",
    icon: "PHP",
    iconUrl: "https://cdn.simpleicons.org/php/777BB4",
  },
  {
    title: "Mobile Optimized",
    text: "Fully responsive stores that look great on every device.",
    icon: "Android",
    iconUrl: "https://cdn.simpleicons.org/android/3DDC84",
  },
  {
    title: "SEO Ready Architecture",
    text: "Built with clean code and SEO best practices for better rankings.",
    icon: "Google",
    iconUrl: "https://cdn.simpleicons.org/google/4285F4",
  },
  {
    title: "Secure Payment Integration",
    text: "Integrated with secure payment gateways worldwide.",
    icon: "Stripe",
    iconUrl: "https://cdn.simpleicons.org/stripe/635BFF",
  },
];

const commerceSolutions = [
  "Shopify Development",
  "WooCommerce Development",
  "Magento Development",
  "Headless Commerce",
  "Marketplace Development",
  "B2B Ecommerce",
  "Multi Vendor Platforms",
  "Subscription Commerce",
];

const processSteps: CardItem[] = [
  { title: "Discovery", text: "Understanding your business & goals", icon: "01" },
  { title: "Strategy", text: "Research & plan for your store", icon: "02" },
  { title: "UI/UX Design", text: "Designing intuitive & beautiful interfaces", icon: "03" },
  { title: "Development", text: "Building fast & scalable solutions", icon: "04" },
  { title: "Testing", text: "Quality assurance & bug fixing", icon: "05" },
  { title: "Launch", text: "Deploy & support your store", icon: "06" },
];

const industries: FeatureItem[] = [
  { title: "Fashion & Apparel", icon: Shirt },
  { title: "Electronics", icon: Smartphone },
  { title: "Healthcare", icon: HeartPulse },
  { title: "Food & Grocery", icon: ShoppingBasket },
  { title: "Furniture", icon: Sofa },
  { title: "Automotive", icon: Car },
  { title: "Education", icon: GraduationCap },
  { title: "Beauty & Cosmetics", icon: Sparkles },
];

const storeFeatures: FeatureItem[] = [
  { title: "Product Management", icon: Package },
  { title: "Inventory Tracking", icon: Warehouse },
  { title: "Payment Gateway Integration", icon: CreditCard },
  { title: "Order Management", icon: ShoppingBag },
  { title: "Shipping Integration", icon: Truck },
  { title: "Wishlist", icon: Heart },
  { title: "Product Reviews", icon: Star },
  { title: "Coupons & Discounts", icon: TicketPercent },
  { title: "Analytics Dashboard", icon: LayoutDashboard },
  { title: "Push Notifications", icon: Bell },
  { title: "Live Chat", icon: MessageCircle },
  { title: "Multi Currency", icon: Coins },
];

const techStack: TechItem[] = [
  { name: "Shopify", logo: "https://cdn.simpleicons.org/shopify/7AB55C" },
  { name: "WooCommerce", logo: "https://cdn.simpleicons.org/woocommerce/96588A" },
  { name: "Magento", logo: "https://cdn.simpleicons.org/magento/EE672F" },
  { name: "Laravel", logo: "https://cdn.simpleicons.org/laravel/FF2D20" },
  { name: "React", logo: "https://cdn.simpleicons.org/react/61DAFB" },
  { name: "Next.js", logo: "https://cdn.simpleicons.org/nextdotjs/111111" },
  { name: "Node.js", logo: "https://cdn.simpleicons.org/nodedotjs/5FA04E" },
  { name: "PHP", logo: "https://cdn.simpleicons.org/php/777BB4" },
  { name: "MySQL", logo: "https://cdn.simpleicons.org/mysql/4479A1" },
  { name: "MongoDB", logo: "https://cdn.simpleicons.org/mongodb/47A248" },
  { name: "AWS", logo: "https://cdn.simpleicons.org/amazonwebservices/232F3E" },
  { name: "Stripe", logo: "https://cdn.simpleicons.org/stripe/635BFF" },
];

const startupChecklist = [
  "Domain & Hosting",
  "Product Catalog",
  "Secure Checkout",
  "Payment Gateway",
  "Shipping Setup",
  "Inventory System",
  "Order Tracking",
  "Mobile Responsive",
  "Live Chat",
  "Email Automation",
  "SEO Setup",
  "Analytics",
];

const successStories = [
  {
    title: "Fashion Brand",
    metric: "+250%",
    label: "Revenue Growth",
    bars: [45, 62, 88, 74, 96],
  },
  {
    title: "Electronics Store",
    metric: "+180%",
    label: "Conversion Rate",
    bars: [34, 48, 60, 82, 92],
  },
  {
    title: "Healthcare Store",
    metric: "+300%",
    label: "Online Orders",
    bars: [26, 42, 68, 78, 98],
  },
];

const testimonials = [
  {
    name: "Anjali Mehta",
    company: "D2C Lifestyle",
    review:
      "WebTech built our Shopify store beautifully. Sales increased significantly within 3 months.",
    avatar: "AM",
  },
  {
    name: "Rohan Verma",
    company: "Electronics Hub",
    review:
      "Excellent team. They understood our requirements and delivered beyond expectations.",
    avatar: "RV",
  },
  {
    name: "Priya Nair",
    company: "Wellness Mart",
    review:
      "Professional, responsive and highly skilled. Highly recommended for eCommerce development.",
    avatar: "PN",
  },
];

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const AnimatedSection: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <motion.section
    className={className}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-90px" }}
    variants={fadeUp}
    transition={{ duration: 0.65, ease: "easeOut" }}
  >
    {children}
  </motion.section>
);

const SectionHeader: React.FC<{
  eyebrow: string;
  title: string;
  text?: string;
  light?: boolean;
}> = ({ eyebrow, title, text, light = false }) => (
  <div className={`ecp-section-header ${light ? "ecp-section-header--light" : ""}`}>
    <span>{eyebrow}</span>
    <h2>{title}</h2>
    {text && <p>{text}</p>}
  </div>
);

const HeroDashboard = () => (
  <motion.div
    className="ecp-dashboard"
    animate={{ y: [0, -14, 0] }}
    transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
  >
    <div className="ecp-dashboard__sidebar" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
    </div>
    <div className="ecp-dashboard__top">
      <div>
        <span>Total Sales</span>
        <strong>$85,680</strong>
      </div>
      <div>
        <span>Orders</span>
        <strong>2,548</strong>
      </div>
    </div>
    <div className="ecp-chart" aria-label="Revenue chart">
      {[44, 58, 51, 72, 66, 88, 80, 96].map((height, index) => (
        <span key={index} style={{ height: `${height}%` }} />
      ))}
    </div>
    <div className="ecp-dashboard__products">
      {["Sneaker", "Bag", "Watch"].map((item) => (
        <div className="ecp-product-card" key={item}>
          <div className="ecp-product-card__image" />
          <span>{item}</span>
          <strong>$99</strong>
        </div>
      ))}
    </div>
    <div className="ecp-floating-pill ecp-floating-pill--sales">
      <span>Revenue</span>
      <strong>+24.6%</strong>
    </div>
    <div className="ecp-floating-pill ecp-floating-pill--orders">
      <span>Today Sales</span>
      <strong>$18,560</strong>
    </div>
  </motion.div>
);

const ECommerceDevelopmentPage: React.FC = () => {
  useEffect(() => {
    document.title =
      "E-Commerce Development Solutions That Scale | WebTech Bangalore";

    const description =
      "Build high-converting online stores with WebTech's premium eCommerce development services in Bangalore.";
    let meta = document.querySelector<HTMLMetaElement>(
      'meta[name="description"]'
    );
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = description;
  }, []);

  return (
    <Fragment>
      <div className="ecp-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            <section className="ecp-hero" aria-labelledby="ecommerce-hero-title">
              <div className="ecp-hero__mesh" aria-hidden="true" />
              <div className="ecp-particles" aria-hidden="true">
                {[...Array(18)].map((_, index) => (
                  <span key={index} />
                ))}
              </div>

              <div className="ecp-container ecp-hero__grid">
                <motion.div
                  className="ecp-hero__content"
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  transition={{ duration: 0.75, ease: "easeOut" }}
                >
                  <span className="ecp-eyebrow">E-Commerce Development</span>
                  <h1 id="ecommerce-hero-title">
                    E-Commerce Development Solutions <span>That Scale</span>
                  </h1>
                  <p>
                    Build high-converting online stores with modern technology,
                    seamless user experiences, and enterprise-grade performance.
                  </p>
                  <div className="ecp-hero__actions">
                    <Link className="ecp-btn ecp-btn--primary" to="/contact">
                      Get Free Consultation
                    </Link>
                    <Link className="ecp-btn ecp-btn--secondary" to="/project">
                      View Our Portfolio
                    </Link>
                  </div>
                </motion.div>

                <div className="ecp-hero__visual">
                  <HeroDashboard />
                </div>
              </div>
            </section>

            <AnimatedSection className="ecp-trust">
              <div className="ecp-container">
                <motion.div className="ecp-stats" variants={stagger}>
                  {stats.map((stat) => (
                    <motion.div className="ecp-stat-card" variants={fadeUp} key={stat.label}>
                      <span>{stat.icon}</span>
                      <strong>{stat.value}</strong>
                      <p>{stat.label}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section">
              <div className="ecp-container">
                <SectionHeader
                  eyebrow="Why Choose WebTech?"
                  title="Why Choose WebTech?"
                />
                <motion.div className="ecp-card-grid ecp-card-grid--six" variants={stagger}>
                  {whyChooseUs.map((item) => (
                    <motion.article className="ecp-glass-card" variants={fadeUp} key={item.title}>
                      <div className="ecp-icon">
                        {item.iconUrl ? (
                          <img src={item.iconUrl} alt={`${item.icon} logo`} loading="lazy" />
                        ) : (
                          item.icon
                        )}
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--light">
              <div className="ecp-container ecp-split">
                <div className="ecp-illustration-panel" aria-hidden="true">
                  <div className="ecp-storefront">
                    <div className="ecp-storefront__awning" />
                    <div className="ecp-storefront__body">
                      <div className="ecp-storefront__screen" />
                      <div className="ecp-storefront__box" />
                      <div className="ecp-storefront__cart" />
                    </div>
                  </div>
                </div>
                <div>
                  <SectionHeader
                    eyebrow="eCommerce Solutions"
                    title="eCommerce Solutions For Every Business"
                    text="We build feature-rich, scalable and secure eCommerce solutions that drive growth and deliver exceptional user experiences."
                    light
                  />
                  <div className="ecp-check-card-grid">
                    {commerceSolutions.map((solution) => (
                      <div className="ecp-check-card" key={solution}>
                        <CheckIcon />
                        <span>{solution}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--white">
              <div className="ecp-container">
                <SectionHeader
                  eyebrow="Our Development Process"
                  title="From idea to launch with a proven workflow"
                  light
                />
                <div className="ecp-timeline">
                  {processSteps.map((step) => (
                    <article className="ecp-timeline__item" key={step.title}>
                      <div className="ecp-timeline__icon">{step.icon}</div>
                      <h3>{step.title}</h3>
                      <p>{step.text}</p>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--white">
              <div className="ecp-container">
                <SectionHeader
                  eyebrow="Industries We Serve"
                  title="Specialized commerce experiences across sectors"
                  light
                />
                <motion.div className="ecp-industry-grid" variants={stagger}>
                  {industries.map(({ title, icon: Icon }) => (
                    <motion.article
                      className="ecp-industry-card"
                      variants={fadeUp}
                      key={title}
                    >
                      <div className="ecp-industry-card__icon">
                        <Icon size={26} strokeWidth={2} aria-hidden="true" />
                      </div>
                      <h3>{title}</h3>
                    </motion.article>
                  ))}
                </motion.div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section">
              <div className="ecp-container">
                <SectionHeader
                  eyebrow="Powerful Features"
                  title="Powerful Features For Your Online Store"
                />
                <div className="ecp-feature-grid">
                  {storeFeatures.map(({ title, icon: Icon }) => (
                    <article className="ecp-feature-card" key={title}>
                      <div className="ecp-mini-icon">
                        <Icon size={22} strokeWidth={2.2} aria-hidden="true" />
                      </div>
                      <h3>{title}</h3>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--compact">
              <div className="ecp-container">
                <SectionHeader eyebrow="Technology Stack We Use" title="Technology Stack We Use" />
                <div className="ecp-tech-grid">
                  {techStack.map((tech) => (
                    <div className="ecp-tech-card" key={tech.name}>
                      <img src={tech.logo} alt={`${tech.name} logo`} loading="lazy" />
                      <span>{tech.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section">
              <div className="ecp-container">
                <SectionHeader
                  eyebrow="Startup Ecommerce Checklist"
                  title="Launch-ready foundations in one premium dashboard"
                />
                <div className="ecp-checklist-dashboard">
                  {startupChecklist.map((item) => (
                    <div className="ecp-dashboard-check" key={item}>
                      <CheckIcon />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--compact">
              <div className="ecp-container">
                <SectionHeader eyebrow="Success Stories" title="Revenue-focused results for growing brands" />
                <div className="ecp-story-grid">
                  {successStories.map((story) => (
                    <article className="ecp-story-card" key={story.title}>
                      <h3>{story.title}</h3>
                      <strong>{story.metric}</strong>
                      <p>{story.label}</p>
                      <div className="ecp-story-chart" aria-hidden="true">
                        {story.bars.map((bar, index) => (
                          <span key={index} style={{ height: `${bar}%` }} />
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-section ecp-section--compact">
              <div className="ecp-container">
                <SectionHeader eyebrow="What Our Clients Say" title="Trusted by ecommerce founders and teams" />
                <div className="ecp-testimonial-row">
                  {testimonials.map((testimonial) => (
                    <article className="ecp-testimonial-card" key={testimonial.name}>
                      <div className="ecp-testimonial-card__head">
                        <div className="ecp-avatar">{testimonial.avatar}</div>
                        <div>
                          <h3>{testimonial.name}</h3>
                          <span>{testimonial.company}</span>
                        </div>
                      </div>
                      <div className="ecp-rating" aria-label="5 out of 5 rating">
                        5.0 / 5
                      </div>
                      <p>{testimonial.review}</p>
                    </article>
                  ))}
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection className="ecp-cta">
              <div className="ecp-container ecp-cta__inner">
                <div>
                  <span>Ready to Launch?</span>
                  <h2>Ready to Launch Your Ecommerce Store?</h2>
                  <p>
                    Let's build a powerful online store that drives sales and
                    grows your business.
                  </p>
                </div>
                <Link className="ecp-btn ecp-btn--primary" to="/contact">
                  Start Your Project
                </Link>
              </div>
            </AnimatedSection>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default ECommerceDevelopmentPage;
